// Upselling types and interfaces - Matches API Response from /api/v1/dashboard/upselling-insights

import {
  IFoodMenuCategory,
  ISubCategoriesJson,
} from "../../../redux/food/food.types";

// ============ COMMON TYPES ============

export interface JSONObject {
  [key: string]:
    | string
    | number
    | boolean
    | null
    | undefined
    | JSONObject
    | JSONObject[];
}

export interface IUpsellPayload {
  company_outlet_uuid: string;
  venue_templates: number | null;
  service_templates: number | null;
  leads_uuid?: string | null;
  others?: JSONObject | null;
}

// ============ SERVICE RELATED TYPES ============

// Variant type for service items
export interface IServiceVariant {
  type: string;
  price: number;
  service_aline_uuid?: string;
}

// Attachment type for service items
export interface IServiceAttachment {
  key: string;
  file: {
    path: string;
    relativePath: string;
  };
  name: string;
  path: string | null;
}

// Preferred event type
export interface IPreferredEvent {
  event_name: string;
  event_type: string;
  event_uuid: string;
}

// Upselling Service Item - nested inside lead's upselling_services array
export interface IUpsellingServiceItem {
  status: string;
  variant: IServiceVariant[];
  create_ts: string;
  insert_ts: string;
  attachments: IServiceAttachment[];
  description: string;
  total_price: number | null;
  fill_quantity: number;
  reference_url: string[];
  total_quantity: number;
  created_by_name: string;
  created_by_uuid: string;
  preferred_event: IPreferredEvent[];
  rule_for_upsell: string[];
  modified_by_name: string;
  modified_by_uuid: string;
  service_item_name: string;
  service_item_uuid: string;
  company_outlet_uuid: string;
  service_category_name: string;
  service_category_uuid: string;
  service_item_unique_id: number;
}

// ============ VENUE RELATED TYPES ============

// Upselling Venue - nested inside lead's upselling_venue object
export interface IUpsellingVenueItem {
  status: string;
  capacity: number;
  create_ts: string;
  insert_ts: string;
  venue_city: string | null;
  venue_name: string;
  venue_uuid: string;
  branch_name: string | null;
  branch_uuid: string | null;
  venue_state: string | null;
  venue_address: string | null;
  venue_country: string | null;
  venue_pincode: string | null;
  created_by_name: string;
  created_by_uuid: string;
  rule_for_upsell: string | null;
  venue_unique_id: number;
  modified_by_name: string;
  modified_by_uuid: string;
  seating_capacity: number;
  floating_capacity: number;
  company_outlet_uuid: string;
}

// ============ FODD RELATED TYPES ============
// Upselling Food Item - nested inside lead's upselling_food_item array
export interface IUpsellingFoodItem {
  food_item_uuid: string;
  food_item_unique_id?: number;

  item_name: string;
  item_description?: string | null;

  item_icon?: string | null;
  item_accent_color?: string | null;

  type?: string | null;
  food_type?: string | null;

  status: "ACTIVE" | "INACTIVE";

  upsell_item?: number;
  rule_for_upsell?: string[] | null;

  category_list?: IFoodMenuCategory[] | null;
  sub_category_list?: ISubCategoriesJson[] | null;

  food_items?: any | null;

  create_ts?: string;
  insert_ts?: string;

  created_by_name?: string | null;
  created_by_uuid?: string | null;
  modified_by_name?: string | null;
  modified_by_uuid?: string | null;

  company_outlet_uuid: string;
}

// ============ LEAD TYPES ============

// Lead object inside service category's leads array
export interface IServiceCategoryLead {
  status: string;
  event_time: string;
  event_type: string;
  event_uuid: string;
  leads_uuid: string;
  country_code: string;
  followup_date: string | null;
  function_date: string;
  name_of_guest: string;
  preferred_venue: string;
  other_event_name: string | null;
  reason_for_upsell: string;
  contact_number_one: string;
  upselling_services: IUpsellingServiceItem[];
  company_outlet_uuid: string;
  handled_by_employee: string;
}

// Lead object inside venue's leads array
export interface IVenueLead {
  status: string;
  event_time: string;
  event_type: string;
  event_uuid: string;
  leads_uuid: string;
  country_code: string;
  followup_date: string | null;
  function_date: string;
  name_of_guest: string;
  preferred_venue: string;
  upselling_venue: IUpsellingVenueItem;
  other_event_name: string | null;
  reason_for_upsell: string;
  contact_number_one: string;
  company_outlet_uuid: string;
  handled_by_employee: string;
}

// Lead object inside venue's leads array
export interface IFoodLead {
  status: string;
  event_time: string;
  event_type: string;
  event_uuid: string;
  leads_uuid: string;
  country_code: string;
  followup_date: string | null;
  function_date: string;
  name_of_guest: string;
  preferred_venue: string;
  upselling_food_item: IUpsellingFoodItem;
  other_event_name: string | null;
  reason_for_upsell: string;
  contact_number_one: string;
  company_outlet_uuid: string;
  handled_by_employee: string;
}
// ============ CARD DATA STRUCTURES ============

// Service Category card data structure
export interface IServiceCategoryCard {
  service_category_uuid: string;
  service_category_name: string;
  master_service_category_uuid: string;
  company_outlet_uuid: string;
  leads: IServiceCategoryLead[];
}

// Venue card data structure
export interface IVenueCard {
  venue_uuid: string;
  venue_name: string;
  leads: IVenueLead[];
}

// Venue card data structure
export interface IFoodCard {
  food_item_uuid: string;
  item_name: string;
  sub_category_list: ISubCategoriesJson[];
  leads: IFoodLead[];
}

// ============ UNION TYPES AND TYPE GUARDS ============

// Union type for the mixed data array (both services and venues in same response)
export type IUpsellingDataItem = IServiceCategoryCard | IVenueCard | IFoodCard;

// Type guards to check item type
export const isServiceCategoryCard = (
  item: IUpsellingDataItem,
): item is IServiceCategoryCard => {
  return "service_category_uuid" in item;
};

export const isVenueCard = (item: IUpsellingDataItem): item is IVenueCard => {
  return "venue_uuid" in item;
};

export const isFoodCard = (item: IUpsellingDataItem): item is IFoodCard => {
  return "food_item_uuid" in item;
};

// ============ API RESPONSE STRUCTURE ============

export interface IUpsellingDashboardResponse {
  message: string;
  data: IUpsellingDataItem[];
}

export type IUpsellInsight = IUpsellingDataItem;
